import { useEffect, useState } from "react";
import { useLang } from "../../../shared/i18n/useLang";
import { ConfirmModal, useToast } from "../../../shared/components/SharedUI";
import {
  MOTIVOS, crearMiAutorizado, crearMiTemporal, editarMiAutorizado, editarMiTemporal, hora,
  misAutorizados, misTemporales, quitarMiAutorizado, quitarMiTemporal, texto as textoDe, textoMotivo,
} from "../../seguridad/services/seguridadService";

/**
 * Portal del residente: quién puede entrar a su casa.
 *
 *  · Visitas por días: alguien que viene ciertos días (el fontanero el martes,
 *    la familia el fin de semana). El guarda la ve en "Autorizados de hoy" y
 *    registra su ingreso con un toque.
 *  · Autorizados permanentes: familia, inquilinos y empleados. Son los mismos
 *    que carga la administración en Residentes; al agregar o quitar uno, la
 *    administración recibe un aviso.
 */

// Fechas en hora local (toISOString las correría a UTC).
function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function sumarDias(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function aFecha(s) {
  return new Date(`${s}T00:00:00`);
}

const TIPOS_AUT = ["Familiar", "Inquilino", "Empleado"];
const VACIO_TEMP = { nombre: "", identificacion: "", placa: "", motivo: "VISITA", desde: "", hasta: "", notas: "" };
const VACIO_AUT = { cedula: "", nombre: "", apellidos: "", tipo: "Familiar", vehiculos: [] };

export default function PortalAutorizados() {
  const L = useLang();
  const { showToast, toastEl } = useToast();
  const [tab, setTab] = useState("temporales");
  const [temporales, setTemporales] = useState([]);
  const [autorizados, setAutorizados] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [form, setForm] = useState(null);        // { tipo: "temp"|"aut", id?, datos }
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [borrar, setBorrar] = useState(null);    // { tipo, id, nombre }

  function cargar() {
    return Promise.all([
      misTemporales().then(setTemporales),
      misAutorizados().then(setAutorizados),
    ]).catch((e) => showToast(e.message, false)).finally(() => setCargando(false));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const hoy = iso(new Date());
  const vigentes = temporales.filter((t) => t.hasta >= hoy);
  const vencidas = temporales.filter((t) => t.hasta < hoy).reverse();

  function nuevaVisita() {
    setErrores({});
    setForm({ tipo: "temp", datos: { ...VACIO_TEMP, desde: hoy, hasta: hoy } });
  }
  function editarVisita(t) {
    setErrores({});
    setForm({ tipo: "temp", id: t.id, datos: {
      nombre: t.nombre, identificacion: t.identificacion, placa: t.placa, motivo: t.motivo,
      desde: t.desde, hasta: t.hasta, notas: t.notas,
    } });
  }
  function nuevoAutorizado() {
    setErrores({});
    setForm({ tipo: "aut", datos: { ...VACIO_AUT } });
  }
  function editarAutorizado(a) {
    setErrores({});
    setForm({ tipo: "aut", id: a.id, datos: {
      cedula: a.cedula, nombre: a.nombre, apellidos: a.apellidos, tipo: a.tipo,
      vehiculos: a.vehiculos.map((v) => ({ placa: v.placa, extranjera: v.extranjera })),
    } });
  }

  async function guardar() {
    setGuardando(true);
    setErrores({});
    const { tipo, id, datos } = form;
    try {
      if (tipo === "temp") {
        await (id ? editarMiTemporal(id, datos) : crearMiTemporal(datos));
        showToast(id ? L("Visita actualizada.", "Visit updated.")
                     : L("Listo. El guarda ya la puede ver.", "Done. The guard can see it now."));
      } else {
        const limpio = { ...datos, vehiculos: datos.vehiculos.filter((v) => v.placa.trim()) };
        await (id ? editarMiAutorizado(id, limpio) : crearMiAutorizado(limpio));
        showToast(id ? L("Autorizado actualizado.", "Authorized person updated.")
                     : L("Autorizado agregado.", "Authorized person added."));
      }
      setForm(null);
      cargar();
    } catch (e) {
      setErrores(e.data?.errores || {});
      showToast(e.message, false);
    } finally {
      setGuardando(false);
    }
  }

  async function confirmarBorrar() {
    try {
      if (borrar.tipo === "temp") await quitarMiTemporal(borrar.id);
      else await quitarMiAutorizado(borrar.id);
      showToast(L("Quitado.", "Removed."));
      cargar();
    } catch (e) {
      showToast(e.message, false);
    } finally {
      setBorrar(null);
    }
  }

  return (
    <>
      <style>{CSS}</style>
      {toastEl}
      <ConfirmModal
        open={!!borrar}
        message={borrar && (borrar.tipo === "temp"
          ? L(`¿Quitar la autorización de ${borrar.nombre}? El guarda ya no la verá.`,
              `Remove the authorization for ${borrar.nombre}? The guard will no longer see it.`)
          : L(`¿Quitar a ${borrar.nombre} de tus autorizados? La administración recibe un aviso.`,
              `Remove ${borrar.nombre} from your authorized people? The administration is notified.`))}
        onConfirm={confirmarBorrar}
        onCancel={() => setBorrar(null)}
      />

      <div className="pa-page">
        <div className="pa-hero">
          <div className="pa-hero-icono"><i className="fa-solid fa-user-shield" /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1>{L("Mis autorizados", "My authorized people")}</h1>
            <p>{L("Las personas que el guarda deja pasar a tu casa.", "The people the guard lets into your home.")}</p>
          </div>
        </div>

        <div className="pa-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "temporales"}
            className={tab === "temporales" ? "activo" : ""} onClick={() => { setTab("temporales"); setForm(null); }}>
            <i className="fa-solid fa-calendar-day" /> {L("Visitas por días", "Visits by day")}
            {vigentes.length > 0 && <span className="pa-contador">{vigentes.length}</span>}
          </button>
          <button type="button" role="tab" aria-selected={tab === "permanentes"}
            className={tab === "permanentes" ? "activo" : ""} onClick={() => { setTab("permanentes"); setForm(null); }}>
            <i className="fa-solid fa-users" /> {L("Permanentes", "Permanent")}
            {autorizados.length > 0 && <span className="pa-contador">{autorizados.length}</span>}
          </button>
        </div>

        {cargando ? (
          <div className="pa-card pa-vacio"><i className="fa-solid fa-circle-notch fa-spin" /> {L("Cargando…", "Loading…")}</div>
        ) : tab === "temporales" ? (
          <>
            {form?.tipo === "temp" ? (
              <FormTemporal form={form} setForm={setForm} errores={errores} guardando={guardando}
                onGuardar={guardar} onCancelar={() => setForm(null)} hoy={hoy} />
            ) : (
              <button type="button" className="pa-btn pa-btn-primario pa-btn-bloque" onClick={nuevaVisita}>
                <i className="fa-solid fa-circle-plus" /> {L("Autorizar una visita", "Authorize a visit")}
              </button>
            )}

            {vigentes.length === 0 && !form && (
              <div className="pa-card pa-vacio">
                <i className="fa-solid fa-calendar-plus" />
                <strong>{L("No tenés visitas autorizadas", "You have no authorized visits")}</strong>
                {L("¿Viene alguien en estos días? Autorizalo y el guarda lo va a tener en su lista.",
                   "Is someone coming over? Authorize them and the guard will have them on the list.")}
              </div>
            )}
            {vigentes.map((t) => (
              <TemporalCard key={t.id} t={t} hoy={hoy}
                onEditar={() => editarVisita(t)}
                onQuitar={() => setBorrar({ tipo: "temp", id: t.id, nombre: t.nombre })} />
            ))}

            {vencidas.length > 0 && (
              <>
                <div className="pa-seccion">{L("Vencidas (última semana)", "Expired (last week)")}</div>
                {vencidas.map((t) => <TemporalCard key={t.id} t={t} hoy={hoy} vencida />)}
              </>
            )}
          </>
        ) : (
          <>
            {form?.tipo === "aut" ? (
              <FormAutorizado form={form} setForm={setForm} errores={errores} guardando={guardando}
                onGuardar={guardar} onCancelar={() => setForm(null)} />
            ) : (
              <button type="button" className="pa-btn pa-btn-primario pa-btn-bloque" onClick={nuevoAutorizado}>
                <i className="fa-solid fa-user-plus" /> {L("Agregar autorizado", "Add authorized person")}
              </button>
            )}
            <div className="pa-nota">
              <i className="fa-solid fa-circle-info" />
              {L("Familia, inquilinos o empleados que entran siempre. Si alguien viene solo unos días, usá \"Visitas por días\".",
                 "Family, tenants or employees who always come in. If someone only comes for a few days, use \"Visits by day\".")}
            </div>
            {autorizados.length === 0 && !form && (
              <div className="pa-card pa-vacio">
                <i className="fa-solid fa-users" />
                <strong>{L("Todavía no hay autorizados", "No authorized people yet")}</strong>
              </div>
            )}
            {autorizados.map((a) => (
              <div key={a.id} className="pa-card pa-item">
                <div className="pa-avatar"><i className="fa-solid fa-user-check" /></div>
                <div className="pa-item-info">
                  <strong>{a.nombre_completo}</strong>
                  <div className="pa-fila">
                    <span className="pa-tag marca">{textoDe(a.tipo)}</span>
                    <small>{a.cedula}</small>
                    {a.vehiculos.map((v) => <span key={v.id} className="pa-placa">{v.placa}</span>)}
                  </div>
                </div>
                <div className="pa-acciones">
                  <button type="button" className="pa-icono" onClick={() => editarAutorizado(a)} title={L("Editar", "Edit")}
                    aria-label={L("Editar", "Edit")}><i className="fa-solid fa-pen" /></button>
                  <button type="button" className="pa-icono peligro" title={L("Quitar", "Remove")} aria-label={L("Quitar", "Remove")}
                    onClick={() => setBorrar({ tipo: "aut", id: a.id, nombre: a.nombre_completo })}>
                    <i className="fa-solid fa-trash" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
}

// ── Una visita autorizada ────────────────────────────────────────────────────

function TemporalCard({ t, hoy, vencida = false, onEditar, onQuitar }) {
  const L = useLang();
  const fmt = (s) => aFecha(s).toLocaleDateString(L("es-CR", "en-US"), { weekday: "short", day: "numeric", month: "short" });
  const manana = iso(sumarDias(aFecha(hoy), 1));
  const dia = (s) => (s === hoy ? L("Hoy", "Today") : s === manana ? L("Mañana", "Tomorrow") : fmt(s));
  const rango = t.desde === t.hasta ? dia(t.desde) : `${dia(t.desde)} - ${dia(t.hasta)}`;
  const empezo = t.desde <= hoy;

  let estado = null;
  if (t.adentro) estado = <span className="pa-tag verde"><i className="fa-solid fa-door-open" /> {L("Está adentro", "Inside now")}</span>;
  else if (t.ingresos_hoy > 0) estado = <span className="pa-tag verde"><i className="fa-solid fa-check" /> {L(`Llegó hoy a las ${hora(t.ultimo_ingreso)}`, `Arrived today at ${hora(t.ultimo_ingreso)}`)}</span>;
  else if (t.ingresos > 0) estado = <span className="pa-tag">{L(`Entró ${t.ingresos} vez/veces`, `Came in ${t.ingresos} time(s)`)}</span>;
  else if (!vencida && empezo) estado = <span className="pa-tag">{L("Todavía no ha llegado", "Not here yet")}</span>;

  return (
    <div className={`pa-card pa-item ${vencida ? "vencida" : ""}`}>
      <div className="pa-avatar"><i className="fa-solid fa-calendar-check" /></div>
      <div className="pa-item-info">
        <strong>{t.nombre}</strong>
        <div className="pa-fila">
          <span className="pa-tag marca"><i className="fa-regular fa-calendar" /> {rango}</span>
          <span className="pa-tag">{textoMotivo(t.motivo)}</span>
          {t.identificacion && <small>{t.identificacion}</small>}
          {t.placa && <span className="pa-placa">{t.placa}</span>}
        </div>
        {t.notas && <div className="pa-notas"><i className="fa-solid fa-note-sticky" /> {t.notas}</div>}
        {estado && <div style={{ marginTop: 6 }}>{estado}</div>}
      </div>
      {!vencida && (
        <div className="pa-acciones">
          <button type="button" className="pa-icono" onClick={onEditar} title={L("Editar", "Edit")} aria-label={L("Editar", "Edit")}>
            <i className="fa-solid fa-pen" />
          </button>
          <button type="button" className="pa-icono peligro" onClick={onQuitar} title={L("Quitar", "Remove")} aria-label={L("Quitar", "Remove")}>
            <i className="fa-solid fa-trash" />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Formulario: visita por días ──────────────────────────────────────────────

function FormTemporal({ form, setForm, errores, guardando, onGuardar, onCancelar, hoy }) {
  const L = useLang();
  const d = form.datos;
  const set = (campo, valor) => setForm((f) => ({ ...f, datos: { ...f.datos, [campo]: valor } }));
  const setRango = (desde, hasta) => setForm((f) => ({ ...f, datos: { ...f.datos, desde, hasta } }));

  // Atajos de días: lo más común con un toque.
  const base = aFecha(hoy);
  const manana = iso(sumarDias(base, 1));
  const dow = base.getDay();                       // 0 domingo ... 6 sábado
  const sabado = dow === 0 ? null : iso(sumarDias(base, 6 - dow));
  const domingo = iso(sumarDias(base, dow === 0 ? 0 : 7 - dow));
  const finde = { desde: sabado && dow !== 6 ? sabado : hoy, hasta: domingo };
  const ATAJOS = [
    { id: "hoy", label: L("Hoy", "Today"), desde: hoy, hasta: hoy },
    { id: "manana", label: L("Mañana", "Tomorrow"), desde: manana, hasta: manana },
    { id: "finde", label: L("Este fin de semana", "This weekend"), ...finde },
    { id: "semana", label: L("Los próximos 7 días", "Next 7 days"), desde: hoy, hasta: iso(sumarDias(base, 6)) },
  ];
  const atajo = ATAJOS.find((a) => a.desde === d.desde && a.hasta === d.hasta)?.id;
  const [otroRango, setOtroRango] = useState(!atajo && !!d.desde);
  const editandoEmpezada = form.id && d.desde < hoy;

  return (
    <div className="pa-card pa-form">
      <div className="pa-form-titulo">
        <i className="fa-solid fa-calendar-plus" />
        {form.id ? L("Editar visita", "Edit visit") : L("Autorizar una visita", "Authorize a visit")}
      </div>

      <Campo label={L("Nombre de la persona", "Person's name")} req error={errores.nombre}>
        <input className="pa-input" value={d.nombre} autoFocus placeholder={L("Ej: Juan Mora (fontanero)", "e.g. John Smith (plumber)")}
          onChange={(e) => set("nombre", e.target.value)} />
      </Campo>

      <div className="pa-grid-2">
        <Campo label={L("Cédula", "ID")} ayuda={L("Opcional, ayuda al guarda", "Optional, helps the guard")}>
          <input className="pa-input" value={d.identificacion} onChange={(e) => set("identificacion", e.target.value)} />
        </Campo>
        <Campo label={L("Placa del vehículo", "Vehicle plate")} ayuda={L("Opcional", "Optional")}>
          <input className="pa-input" value={d.placa} placeholder="ABC-123" style={{ textTransform: "uppercase" }}
            onChange={(e) => set("placa", e.target.value.toUpperCase())} />
        </Campo>
      </div>

      <Campo label={L("¿A qué viene?", "Reason")} error={errores.motivo}>
        <div className="pa-chips">
          {MOTIVOS.map((m) => (
            <button type="button" key={m.value} className={`pa-chip ${d.motivo === m.value ? "activo" : ""}`}
              onClick={() => set("motivo", m.value)}>
              <i className={`fa-solid ${m.icon}`} /> {L(m.es, m.en)}
            </button>
          ))}
        </div>
      </Campo>

      <Campo label={L("¿Qué días?", "Which days?")} req error={errores.desde || errores.hasta}>
        {!editandoEmpezada && (
          <div className="pa-chips">
            {ATAJOS.map((a) => (
              <button type="button" key={a.id} className={`pa-chip ${!otroRango && atajo === a.id ? "activo" : ""}`}
                onClick={() => { setOtroRango(false); setRango(a.desde, a.hasta); }}>
                {a.label}
              </button>
            ))}
            <button type="button" className={`pa-chip ${otroRango ? "activo" : ""}`} onClick={() => setOtroRango(true)}>
              <i className="fa-regular fa-calendar" /> {L("Escoger fechas", "Pick dates")}
            </button>
          </div>
        )}
        {(otroRango || editandoEmpezada) && (
          <div className="pa-grid-2" style={{ marginTop: 10 }}>
            <label className="pa-sublabel">{L("Desde", "From")}
              <input type="date" className="pa-input" value={d.desde} min={editandoEmpezada ? undefined : hoy}
                disabled={editandoEmpezada}
                onChange={(e) => setRango(e.target.value, d.hasta && d.hasta >= e.target.value ? d.hasta : e.target.value)} />
            </label>
            <label className="pa-sublabel">{L("Hasta", "To")}
              <input type="date" className="pa-input" value={d.hasta} min={d.desde > hoy ? d.desde : hoy}
                onChange={(e) => set("hasta", e.target.value)} />
            </label>
          </div>
        )}
      </Campo>

      <Campo label={L("Nota para el guarda", "Note for the guard")} ayuda={L("Opcional", "Optional")}>
        <input className="pa-input" value={d.notas} maxLength={300}
          placeholder={L("Ej: viene en un carro blanco", "e.g. comes in a white car")}
          onChange={(e) => set("notas", e.target.value)} />
      </Campo>

      <div className="pa-form-botones">
        <button type="button" className="pa-btn pa-btn-sec" onClick={onCancelar}>{L("Cancelar", "Cancel")}</button>
        <button type="button" className="pa-btn pa-btn-primario" disabled={guardando} onClick={onGuardar}>
          <i className={`fa-solid ${guardando ? "fa-circle-notch fa-spin" : "fa-check"}`} />
          {form.id ? L("Guardar", "Save") : L("Autorizar", "Authorize")}
        </button>
      </div>
    </div>
  );
}

// ── Formulario: autorizado permanente ────────────────────────────────────────

function FormAutorizado({ form, setForm, errores, guardando, onGuardar, onCancelar }) {
  const L = useLang();
  const d = form.datos;
  const set = (campo, valor) => setForm((f) => ({ ...f, datos: { ...f.datos, [campo]: valor } }));
  const setPlaca = (i, cambios) => set("vehiculos", d.vehiculos.map((v, j) => (j === i ? { ...v, ...cambios } : v)));

  return (
    <div className="pa-card pa-form">
      <div className="pa-form-titulo">
        <i className="fa-solid fa-user-plus" />
        {form.id ? L("Editar autorizado", "Edit authorized person") : L("Agregar autorizado", "Add authorized person")}
      </div>

      <Campo label={L("Cédula", "ID")} req error={errores.cedula}>
        <input className="pa-input" value={d.cedula} autoFocus onChange={(e) => set("cedula", e.target.value)} />
      </Campo>
      <div className="pa-grid-2">
        <Campo label={L("Nombre", "First name")} req error={errores.nombre}>
          <input className="pa-input" value={d.nombre} onChange={(e) => set("nombre", e.target.value)} />
        </Campo>
        <Campo label={L("Apellidos", "Last name")}>
          <input className="pa-input" value={d.apellidos} onChange={(e) => set("apellidos", e.target.value)} />
        </Campo>
      </div>
      <Campo label={L("¿Quién es?", "Who is it?")} req error={errores.tipo}>
        <div className="pa-chips">
          {TIPOS_AUT.map((t) => (
            <button type="button" key={t} className={`pa-chip ${d.tipo === t ? "activo" : ""}`} onClick={() => set("tipo", t)}>
              {textoDe(t)}
            </button>
          ))}
        </div>
      </Campo>
      <Campo label={L("Vehículos", "Vehicles")} ayuda={L("Opcional", "Optional")} error={errores.vehiculos}>
        {d.vehiculos.map((v, i) => (
          <div key={i} className="pa-fila" style={{ flexWrap: "nowrap", marginBottom: 8 }}>
            <input className="pa-input" value={v.placa} placeholder="ABC-123" style={{ textTransform: "uppercase" }}
              onChange={(e) => setPlaca(i, { placa: e.target.value.toUpperCase() })} />
            <label className="pa-check" title={L("Placa de otro país", "Foreign plate")}>
              <input type="checkbox" checked={v.extranjera} onChange={(e) => setPlaca(i, { extranjera: e.target.checked })} />
              {L("Extranjera", "Foreign")}
            </label>
            <button type="button" className="pa-icono peligro" aria-label={L("Quitar placa", "Remove plate")}
              onClick={() => set("vehiculos", d.vehiculos.filter((_, j) => j !== i))}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        ))}
        <button type="button" className="pa-btn pa-btn-sec pa-btn-chico"
          onClick={() => set("vehiculos", [...d.vehiculos, { placa: "", extranjera: false }])}>
          <i className="fa-solid fa-car-side" /> {L("Agregar placa", "Add plate")}
        </button>
      </Campo>

      <div className="pa-nota" style={{ margin: 0 }}>
        <i className="fa-solid fa-bell" />
        {L("La administración recibe un aviso cuando agregás o quitás a alguien.",
           "The administration is notified when you add or remove someone.")}
      </div>

      <div className="pa-form-botones">
        <button type="button" className="pa-btn pa-btn-sec" onClick={onCancelar}>{L("Cancelar", "Cancel")}</button>
        <button type="button" className="pa-btn pa-btn-primario" disabled={guardando} onClick={onGuardar}>
          <i className={`fa-solid ${guardando ? "fa-circle-notch fa-spin" : "fa-check"}`} />
          {L("Guardar", "Save")}
        </button>
      </div>
    </div>
  );
}

function Campo({ label, req, ayuda, error, children }) {
  return (
    <div>
      <label className="pa-label">
        {label}{req && <span style={{ color: "var(--brand)" }}> *</span>}
        {ayuda && <span className="pa-ayuda"> - {ayuda}</span>}
      </label>
      {children}
      {error && <div className="pa-error">{error}</div>}
    </div>
  );
}

// Estilos de la página. Todo con las variables de tema (claro y oscuro) y el
// naranja de marca; los colores de estado tienen su versión oscura para el
// tema claro, donde los tonos pastel no se leen.
const CSS = `
.pa-page { padding: 28px 28px 48px; max-width: 860px; animation: paFade .3s ease both; }
@keyframes paFade { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@media (max-width: 600px) { .pa-page { padding: 16px 14px 40px; } }
.pa-page {
  --pa-ok: #6ee7b7; --pa-ok-bg: rgba(16,185,129,.12); --pa-ok-borde: rgba(16,185,129,.4);
  --pa-error: #fca5a5;
}
[data-theme="light"] .pa-page { --pa-ok: #047857; --pa-error: #b91c1c; }
.pa-page i, .pa-page button * { color: inherit; }

.pa-hero { display: flex; gap: 14px; align-items: center; margin-bottom: 18px; }
.pa-hero h1 { margin: 0; font-size: clamp(20px, 2.5vw, 26px); font-weight: 800; color: var(--text-primary); }
.pa-hero p { margin: 3px 0 0; font-size: 14px; color: var(--text-secondary); }
.pa-hero-icono {
  width: 48px; height: 48px; border-radius: 14px; flex-shrink: 0; font-size: 20px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(var(--brand-rgb), .12); border: 1px solid rgba(var(--brand-rgb), .35); color: var(--brand);
}

.pa-tabs { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; padding: 5px; margin-bottom: 14px;
  background: var(--bg-card); border: 1px solid var(--border-card); border-radius: 14px; }
.pa-tabs button {
  min-height: 46px; border: none; border-radius: 10px; background: transparent; cursor: pointer;
  color: var(--text-secondary); font-size: 14px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.pa-tabs button.activo { background: var(--brand); color: #fff; }
.pa-contador { min-width: 22px; height: 22px; padding: 0 7px; border-radius: 999px; font-size: 12px;
  display: inline-flex; align-items: center; justify-content: center; background: rgba(0,0,0,.15); }

.pa-card { background: var(--bg-card); border: 1px solid var(--border-card); border-radius: 14px;
  padding: 14px 16px; box-shadow: 0 1px 3px var(--shadow); margin-top: 10px; }
.pa-vacio { text-align: center; color: var(--text-secondary); padding: 30px 16px; font-size: 14px; }
.pa-vacio > i { display: block; font-size: 26px; margin-bottom: 10px; color: var(--brand); }
.pa-vacio strong { display: block; color: var(--text-primary); font-size: 15px; margin-bottom: 4px; }
.pa-seccion { margin: 22px 0 2px; font-size: 12px; font-weight: 700; letter-spacing: 1px;
  text-transform: uppercase; color: var(--text-secondary); }
.pa-nota { display: flex; gap: 8px; align-items: flex-start; margin: 12px 0 2px; font-size: 13px;
  color: var(--text-secondary); line-height: 1.45; }
.pa-nota i { margin-top: 3px; }

.pa-item { display: flex; gap: 12px; align-items: flex-start; }
.pa-item.vencida { opacity: .62; }
.pa-avatar { width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0; display: flex;
  align-items: center; justify-content: center; background: rgba(var(--brand-rgb), .1); color: var(--brand); }
.pa-item-info { flex: 1; min-width: 0; }
.pa-item-info strong { display: block; font-size: 15px; color: var(--text-primary); }
.pa-item-info small { color: var(--text-secondary); font-size: 13px; }
.pa-notas { font-size: 13px; color: var(--text-secondary); margin-top: 5px; }
.pa-fila { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; margin-top: 4px; }
.pa-acciones { display: flex; gap: 6px; flex-shrink: 0; }
.pa-icono { width: 38px; height: 38px; border-radius: 10px; border: 1px solid var(--border-card);
  background: var(--bg-input); color: var(--text-secondary); cursor: pointer; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center; }
.pa-icono:hover { color: var(--brand); border-color: rgba(var(--brand-rgb), .45); }
.pa-icono.peligro:hover { color: var(--pa-error); border-color: var(--pa-error); }

.pa-tag { display: inline-flex; align-items: center; gap: 5px; padding: 2px 9px; border-radius: 999px;
  font-size: 12px; font-weight: 700; border: 1px solid var(--border-card); color: var(--text-secondary); }
.pa-tag.marca { color: var(--brand-strong); border-color: rgba(var(--brand-rgb), .45); background: rgba(var(--brand-rgb), .1); }
:root:not([data-theme="light"]) .pa-tag.marca { color: var(--brand); }
.pa-tag.verde { color: var(--pa-ok); border-color: var(--pa-ok-borde); background: var(--pa-ok-bg); }
.pa-placa { font-family: ui-monospace, Menlo, monospace; font-weight: 800; font-size: 12px; padding: 1px 7px;
  border-radius: 6px; background: #f1f5f9; color: #0f172a; border: 1px solid #cbd5e1; }

.pa-btn { min-height: 46px; padding: 10px 18px; border-radius: 12px; border: none; cursor: pointer;
  font-size: 15px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
.pa-btn:disabled { opacity: .55; cursor: not-allowed; }
.pa-btn-primario { background: var(--brand); color: #fff; box-shadow: 0 4px 14px rgba(var(--brand-rgb), .3); }
.pa-btn-primario:hover { background: var(--brand-deep); }
.pa-btn-sec { background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-card); }
.pa-btn-bloque { width: 100%; }
.pa-btn-chico { min-height: 38px; padding: 6px 12px; font-size: 13px; }

.pa-form { display: flex; flex-direction: column; gap: 14px; padding: 18px; border-color: rgba(var(--brand-rgb), .4); }
.pa-form-titulo { font-size: 16px; font-weight: 800; color: var(--text-primary); display: flex; gap: 10px; align-items: center; }
.pa-form-titulo i { color: var(--brand); }
.pa-form-botones { display: flex; gap: 8px; justify-content: flex-end; flex-wrap: wrap; }
.pa-form-botones .pa-btn { flex: 1 1 140px; max-width: 220px; }
.pa-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 560px) { .pa-grid-2 { grid-template-columns: 1fr; } }
.pa-label { display: block; font-size: 13px; font-weight: 700; color: var(--text-secondary); margin-bottom: 6px; }
.pa-ayuda { font-weight: 500; color: var(--text-muted); }
.pa-sublabel { display: flex; flex-direction: column; gap: 4px; font-size: 12px; font-weight: 600; color: var(--text-secondary); }
.pa-input { width: 100%; min-height: 46px; padding: 10px 14px; border-radius: 12px; font-size: 16px;
  background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-input); outline: none; }
.pa-input:focus { border-color: var(--brand); box-shadow: 0 0 0 3px rgba(var(--brand-rgb), .2); }
.pa-input:disabled { opacity: .6; }
.pa-error { color: var(--pa-error); font-size: 13px; margin-top: 5px; }
.pa-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.pa-chip { min-height: 40px; padding: 7px 14px; border-radius: 999px; cursor: pointer; font-size: 14px; font-weight: 600;
  border: 1px solid var(--border-input); background: var(--bg-input); color: var(--text-primary);
  display: inline-flex; align-items: center; gap: 7px; }
.pa-chip.activo { border-color: var(--brand); background: rgba(var(--brand-rgb), .14); color: var(--brand-strong); font-weight: 700; }
:root:not([data-theme="light"]) .pa-chip.activo { color: var(--brand); }
.pa-check { display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--text-secondary); white-space: nowrap; }
`;
